import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconHandMove, IconRotateClockwise } from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { renderPdfPageThumbnails } from '../shared/pdfPreviewEngine';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

const TEXT_FONT = '"Kalpurush", "SolaimanLipi", "Noto Sans Bengali", sans-serif';

function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
}

function measureTextSize(text, fontSize) {
    const safeText = String(text || '').trim() || ' ';
    const size = Number(fontSize) || 48;
    const fontSpec = `700 ${size}px ${TEXT_FONT}`;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = fontSpec;
    const metrics = ctx.measureText(safeText);
    const paddingX = Math.ceil(size * 0.5);
    const paddingY = Math.ceil(size * 0.4);
    return {
        width: Math.max(8, Math.ceil(metrics.width + paddingX * 2)),
        height: Math.max(8, Math.ceil(size * 1.6 + paddingY * 2)),
    };
}

function angleFromCenter(cx, cy, x, y) {
    return (Math.atan2(y - cy, x - cx) * 180) / Math.PI;
}

async function readPdfPageSize(file) {
    const data = new Uint8Array(await file.arrayBuffer());
    const pdf = await pdfjsLib.getDocument({ data }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1 });
    return { width: viewport.width, height: viewport.height };
}

/**
 * Interactive first-page preview fitted to the panel:
 * drag to move, corner to resize, top handle to rotate, double-click text to edit.
 */
export default function WatermarkInteractivePreview({
    file,
    mode,
    text,
    textColor,
    fontSize,
    textOpacity,
    imagePreview,
    imageNaturalSize,
    imageScale,
    imageOpacity,
    xPercent,
    yPercent,
    rotationDegrees,
    onPositionChange,
    onFontSizeChange,
    onImageScaleChange,
    onRotationChange,
    onTextChange,
}) {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const pageRef = useRef(null);
    const textInputRef = useRef(null);
    const dragRef = useRef(null);

    const [viewport, setViewport] = useState({ w: 0, h: 0 });
    const [pdfSize, setPdfSize] = useState(null);
    const [pageSrc, setPageSrc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [editingText, setEditingText] = useState(false);
    const [draftText, setDraftText] = useState('');

    const sourceKey = useMemo(() => {
        if (!file) return null;
        return `${file.name}-${file.size}-${file.lastModified}`;
    }, [file]);

    // Fit the whole page inside the preview panel (width + height).
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const measure = () => {
            setViewport({
                w: Math.max(160, Math.floor(el.clientWidth)),
                h: Math.max(160, Math.floor(el.clientHeight)),
            });
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [sourceKey]);

    useEffect(() => {
        if (!file || !sourceKey) {
            setPdfSize(null);
            return undefined;
        }
        let cancelled = false;
        readPdfPageSize(file)
            .then((size) => {
                if (!cancelled) setPdfSize(size);
            })
            .catch(() => {
                if (!cancelled) setPdfSize(null);
            });
        return () => { cancelled = true; };
    }, [file, sourceKey]);

    const pageWidth = useMemo(() => {
        if (!pdfSize || viewport.w < 160 || viewport.h < 160) return 0;
        const aspect = pdfSize.height / pdfSize.width;
        const maxW = viewport.w - 12;
        const maxH = viewport.h - 56; // hint + rotate-handle padding
        const widthFromHeight = Math.floor(maxH / aspect);
        return Math.max(140, Math.min(maxW, widthFromHeight));
    }, [pdfSize, viewport]);

    useEffect(() => {
        if (!file || !sourceKey || pageWidth < 140) {
            setPageSrc(null);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);

        renderPdfPageThumbnails(file, { maxPages: 1, thumbWidth: pageWidth })
            .then((pages) => {
                if (cancelled || !pages[0]) return;
                setPageSrc(pages[0].src);
            })
            .catch(() => {
                if (!cancelled) setPageSrc(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [file, sourceKey, pageWidth]);

    useEffect(() => {
        if (!editingText) return undefined;
        const id = requestAnimationFrame(() => {
            textInputRef.current?.focus();
            textInputRef.current?.select?.();
        });
        return () => cancelAnimationFrame(id);
    }, [editingText]);

    const hasWatermark = mode === 'text'
        ? !!String(text || '').trim() || editingText
        : !!imagePreview;

    const markSize = useMemo(() => {
        if (!pdfSize || !pageWidth || !hasWatermark) return { w: 0, h: 0 };
        const scale = pageWidth / pdfSize.width;
        const displayText = editingText ? (draftText || ' ') : text;

        if (mode === 'text') {
            const measured = measureTextSize(displayText, fontSize);
            const drawW = Math.min(pdfSize.width * 0.85, measured.width);
            const drawH = (measured.height / measured.width) * drawW;
            return { w: Math.max(48, drawW * scale), h: Math.max(28, drawH * scale) };
        }

        const natW = imageNaturalSize?.width || 1;
        const natH = imageNaturalSize?.height || 1;
        const drawW = pdfSize.width * (Math.min(100, Math.max(5, Number(imageScale) || 25)) / 100);
        const drawH = (natH / natW) * drawW;
        return { w: drawW * scale, h: drawH * scale };
    }, [pdfSize, pageWidth, hasWatermark, mode, text, draftText, editingText, fontSize, imageNaturalSize, imageScale]);

    const pageDisplayH = pdfSize && pageWidth
        ? pageWidth * (pdfSize.height / pdfSize.width)
        : 0;

    const markLeft = pageDisplayH > 0
        ? (clamp(Number(xPercent) || 0, 0, 100) / 100) * Math.max(0, pageWidth - markSize.w)
        : 0;
    const markTop = pageDisplayH > 0
        ? (clamp(Number(yPercent) || 0, 0, 100) / 100) * Math.max(0, pageDisplayH - markSize.h)
        : 0;

    const commitTextEdit = useCallback(() => {
        const next = String(draftText || '').trim();
        if (next) onTextChange?.(next);
        setEditingText(false);
    }, [draftText, onTextChange]);

    const beginDrag = useCallback((e, kind) => {
        if (!hasWatermark || !pageRef.current || editingText) return;
        e.preventDefault();
        e.stopPropagation();
        const pageRect = pageRef.current.getBoundingClientRect();
        const markCx = pageRect.left + markLeft + markSize.w / 2;
        const markCy = pageRect.top + markTop + markSize.h / 2;
        const startAngle = angleFromCenter(markCx, markCy, e.clientX, e.clientY);
        dragRef.current = {
            kind,
            startX: e.clientX,
            startY: e.clientY,
            xPercent: Number(xPercent) || 0,
            yPercent: Number(yPercent) || 0,
            fontSize: Number(fontSize) || 37,
            imageScale: Number(imageScale) || 30,
            rotation: Number(rotationDegrees) || 0,
            startAngle,
            pageW: pageRect.width,
            pageH: pageRect.height,
            markW: markSize.w,
            markH: markSize.h,
            markCx,
            markCy,
        };
        setDragging(true);
        e.currentTarget.setPointerCapture?.(e.pointerId);
    }, [
        hasWatermark,
        editingText,
        xPercent,
        yPercent,
        fontSize,
        imageScale,
        rotationDegrees,
        markSize,
        markLeft,
        markTop,
    ]);

    useEffect(() => {
        if (!dragging) return undefined;

        const onMove = (e) => {
            const d = dragRef.current;
            if (!d) return;
            e.preventDefault();

            if (d.kind === 'move') {
                const rangeX = Math.max(1, d.pageW - d.markW);
                const rangeY = Math.max(1, d.pageH - d.markH);
                const nextX = clamp(d.xPercent + ((e.clientX - d.startX) / rangeX) * 100, 0, 100);
                const nextY = clamp(d.yPercent + ((e.clientY - d.startY) / rangeY) * 100, 0, 100);
                onPositionChange?.({
                    xPercent: Math.round(nextX),
                    yPercent: Math.round(nextY),
                });
                return;
            }

            if (d.kind === 'resize') {
                const dx = e.clientX - d.startX;
                if (mode === 'text') {
                    onFontSizeChange?.(clamp(Math.round(d.fontSize + dx * 0.35), 10, 200));
                } else {
                    onImageScaleChange?.(clamp(Math.round(d.imageScale + (dx / d.pageW) * 100), 5, 100));
                }
                return;
            }

            if (d.kind === 'rotate') {
                const current = angleFromCenter(d.markCx, d.markCy, e.clientX, e.clientY);
                let next = Math.round(d.rotation + (current - d.startAngle));
                while (next > 180) next -= 360;
                while (next < -180) next += 360;
                onRotationChange?.(next);
            }
        };

        const onEnd = () => {
            setDragging(false);
            dragRef.current = null;
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onEnd);
        window.addEventListener('pointercancel', onEnd);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onEnd);
            window.removeEventListener('pointercancel', onEnd);
        };
    }, [dragging, mode, onPositionChange, onFontSizeChange, onImageScaleChange, onRotationChange]);

    const opacity = mode === 'text'
        ? clamp(Number(textOpacity) || 0, 0, 100) / 100
        : clamp(Number(imageOpacity) || 0, 0, 100) / 100;

    const startTextEdit = (e) => {
        if (mode !== 'text') return;
        e.preventDefault();
        e.stopPropagation();
        setDraftText(String(text || ''));
        setEditingText(true);
    };

    return (
        <div ref={containerRef} className="relative flex h-full min-h-0 w-full flex-col overflow-hidden">
            {loading && !pageSrc ? (
                <div className="flex flex-1 items-center justify-center">
                    <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-primary-100 border-t-primary dark:border-primary-900 dark:border-t-primary" />
                </div>
            ) : pageSrc ? (
                <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-1 pb-1 pt-2">
                    <div className="relative mx-auto pt-6" style={{ width: pageWidth || '100%' }}>
                        <div
                            ref={pageRef}
                            className="relative overflow-visible rounded-lg border border-[#ECE8E4] bg-white shadow-[0_4px_12px_rgba(28,24,21,0.08)] dark:border-zinc-700 dark:bg-zinc-900"
                        >
                            <img
                                src={pageSrc}
                                alt=""
                                className="block h-auto w-full select-none bg-white"
                                draggable={false}
                            />

                            {hasWatermark && markSize.w > 0 ? (
                                <div
                                    role="presentation"
                                    className={`absolute z-10 touch-none select-none ${
                                        editingText ? 'cursor-text' : dragging ? 'cursor-grabbing' : 'cursor-grab'
                                    }`}
                                    style={{
                                        left: markLeft,
                                        top: markTop,
                                        width: markSize.w,
                                        height: markSize.h,
                                        transform: `rotate(${Number(rotationDegrees) || 0}deg)`,
                                        transformOrigin: 'center center',
                                    }}
                                    onPointerDown={(e) => {
                                        if (editingText) return;
                                        beginDrag(e, 'move');
                                    }}
                                    onDoubleClick={startTextEdit}
                                    title={t('watermarkPdf.dragHint')}
                                >
                                    <div
                                        className={`relative flex h-full w-full items-center justify-center overflow-visible rounded-md border-2 border-dashed ${
                                            dragging || editingText
                                                ? 'border-pdf-cat-solid bg-pdf-cat-tint/20'
                                                : 'border-pdf-cat-solid/70 bg-transparent hover:border-pdf-cat-solid hover:bg-pdf-cat-tint/10'
                                        }`}
                                    >
                                        {mode === 'text' ? (
                                            editingText ? (
                                                <input
                                                    ref={textInputRef}
                                                    type="text"
                                                    value={draftText}
                                                    onChange={(e) => setDraftText(e.target.value)}
                                                    onBlur={commitTextEdit}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            commitTextEdit();
                                                        } else if (e.key === 'Escape') {
                                                            e.preventDefault();
                                                            setEditingText(false);
                                                        }
                                                    }}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    className="w-[95%] rounded border border-pdf-cat-solid/40 bg-white/95 px-1 py-0.5 text-center font-bold outline-none dark:bg-zinc-900/95"
                                                    style={{
                                                        color: textColor || '#000000',
                                                        fontFamily: TEXT_FONT,
                                                        fontSize: Math.max(10, markSize.h * 0.38),
                                                    }}
                                                    aria-label={t('watermarkPdf.textLabel')}
                                                />
                                            ) : (
                                                <span
                                                    className="pointer-events-none whitespace-nowrap px-1 font-bold leading-none"
                                                    style={{
                                                        color: textColor || '#000000',
                                                        fontFamily: TEXT_FONT,
                                                        fontSize: Math.max(10, markSize.h * 0.42),
                                                        opacity,
                                                    }}
                                                >
                                                    {String(text || '').trim()}
                                                </span>
                                            )
                                        ) : (
                                            <img
                                                src={imagePreview}
                                                alt=""
                                                className="pointer-events-none h-full w-full object-contain"
                                                style={{ opacity }}
                                                draggable={false}
                                            />
                                        )}
                                    </div>

                                    {/* Rotate handle (top center) */}
                                    <button
                                        type="button"
                                        aria-label={t('watermarkPdf.rotateHandle')}
                                        className="absolute left-1/2 top-0 z-20 flex h-5 w-5 -translate-x-1/2 -translate-y-[22px] cursor-grab items-center justify-center rounded-full border-2 border-white bg-pdf-cat-solid text-white shadow-sm active:cursor-grabbing dark:border-zinc-900"
                                        onPointerDown={(e) => beginDrag(e, 'rotate')}
                                    >
                                        <IconRotateClockwise size={11} stroke={2.4} />
                                    </button>
                                    <span
                                        aria-hidden="true"
                                        className="pointer-events-none absolute left-1/2 top-0 h-[14px] w-px -translate-x-1/2 -translate-y-[14px] bg-pdf-cat-solid/70"
                                    />

                                    {/* Resize handle (bottom-right) */}
                                    <button
                                        type="button"
                                        aria-label={t('watermarkPdf.resizeHandle')}
                                        className="absolute -bottom-1.5 -right-1.5 z-20 h-4 w-4 cursor-nwse-resize rounded-full border-2 border-white bg-pdf-cat-solid shadow-sm dark:border-zinc-900"
                                        onPointerDown={(e) => beginDrag(e, 'resize')}
                                    />
                                </div>
                            ) : null}
                        </div>

                        <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-[11px] font-medium text-[#A8A099] dark:text-zinc-500">
                            <IconHandMove size={13} stroke={1.8} />
                            {t('watermarkPdf.dragHint')}
                        </p>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
